<script setup lang="ts">
  // Read-only preview of an incoming (Received) order with Aceptar/Rechazar
  // in the footer — same handlers/contracts as PosV2IncomingPane (plan §5.4).
  // Opened via showDynamicDialog from the incoming cards.
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import { OrderStatus } from '@/enums/OrderEnums';
  import { AlertType } from '@/interfaces/IAlert';
  import { IOrder } from '@/interfaces/IOrder';
  import { maybePromptCoversAfterAccept } from '@/composition/covers-accept-prompt';
  import { useModal } from '@/composition/use-modal';
  import { useOrders } from '@/composition/use-orders';
  import { deviceCanEverPrint, usePrinting } from '@/composition/use-printing';
  import { useUi } from '@/composition/use-ui';
  import { useOrdersStore } from '@/stores/orders.store';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosV2ViewWorkspaceBase from '@/components/pos-v2/view/PosV2ViewWorkspaceBase.vue';

  export interface IPosV2IncomingPreviewResult {
    accepted?: boolean;
    rejected?: boolean;
  }

  const props = defineProps<{
    orderId: number;
  }>();

  const MODAL_NAME = 'pos-v2-incoming-preview';

  const modals = useModal();
  const ordersHelper = useOrders();
  const ordersStore = useOrdersStore();
  const printing = usePrinting();
  const ui = useUi();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  /** Live incoming instance — disappears once accepted/rejected elsewhere. */
  const incomingOrder = computed<IOrder | null>(() => {
    const orders = ordersStore.newOrders;
    if (!orders) return null;
    return orders.find((o) => o.id === props.orderId) ?? null;
  });

  // Snapshot kept for display so the preview doesn't blank out mid-action.
  const displayOrder = ref<IOrder | null>(null);

  watch(
    incomingOrder,
    (o) => {
      if (o) displayOrder.value = o;
    },
    { immediate: true }
  );

  const accepting = ref(false);
  const rejecting = ref(false);
  const printingSummary = ref(false);
  // printingSummary is part of the gate for renderer safety, not just UX: if
  // Accept/Reject stayed enabled during a pending print, dialogSubmit could
  // unmount this component while printOrderSummary is in flight, and the
  // finally below would then flip :loading during teardown.
  const isMutating = computed(
    () => accepting.value || rejecting.value || printingSummary.value
  );

  const canAccept = computed(() => !!incomingOrder.value && !isMutating.value);
  const canReject = computed(() => !!incomingOrder.value && !isMutating.value);

  // Renderer safety: dialogSubmit unmounts this component, so the loading
  // flags are only reset on the paths where the modal stays open.

  async function handleAccept() {
    const order = incomingOrder.value;
    if (!order) {
      ui.showToast(AlertType.Info, i18nT('posV2.orderNoLongerAvailable'));
      return;
    }
    if (isMutating.value) return;

    accepting.value = true;
    let didUpdate = false;
    try {
      didUpdate = await ordersHelper.setOrderStatus(
        order,
        OrderStatus.Progress
      );
    } catch (error) {
      ui.serverError(error, i18nT('posV2.statusUpdateError'));
    }

    if (!didUpdate) {
      accepting.value = false;
      return;
    }

    ui.showToast(AlertType.Success, i18nT('posV2.orderAccepted'), {
      text: i18nT('posV2.viewOrder'),
      cb: () => ordersHelper.openOrder(order),
    });
    modals.dialogSubmit(MODAL_NAME, <IPosV2IncomingPreviewResult>{
      accepted: true,
    });
    // After dialogSubmit: this preview is closing, the covers prompt opens as
    // its own dialog ($modal is app-global, safe past unmount). Fire-and-forget.
    void maybePromptCoversAfterAccept(proxy.$modal, order);
  }

  async function handleReject() {
    const order = incomingOrder.value;
    if (!order) {
      ui.showToast(AlertType.Info, i18nT('posV2.orderNoLongerAvailable'));
      return;
    }
    if (isMutating.value) return;

    const confirmed = await modals.confirm({
      title: i18nT('posV2.rejectOrderTitle'),
      message: i18nT('posV2.rejectOrderConfirm'),
    });
    // Re-check: the order may have changed while the confirm dialog was open.
    if (!confirmed || isMutating.value || !incomingOrder.value) return;

    rejecting.value = true;
    let didUpdate = false;
    try {
      // setOrderStatus returns false when a guard blocks the transition
      // (e.g. declining a paid order) — the modal must stay open then.
      didUpdate = await ordersHelper.setOrderStatus(
        order,
        OrderStatus.Declined
      );
    } catch (error) {
      ui.serverError(error, i18nT('posV2.statusUpdateError'));
    }

    if (!didUpdate) {
      rejecting.value = false;
      return;
    }

    modals.dialogSubmit(MODAL_NAME, <IPosV2IncomingPreviewResult>{
      rejected: true,
    });
  }

  async function handlePrintSummary() {
    if (isMutating.value) return;

    const order = incomingOrder.value;
    if (!order) {
      ui.showToast(AlertType.Info, i18nT('posV2.orderNoLongerAvailable'));
      return;
    }

    printingSummary.value = true;
    try {
      await printing.printOrderSummary(order);
    } catch (error) {
      ui.serverError(error, i18nT('posV2.printSummaryError'));
    } finally {
      // Safe: while printingSummary is set, isMutating blocks every unmount
      // path (accept/reject/close), so the modal is still mounted here.
      printingSummary.value = false;
    }
  }

  function handleClose() {
    if (isMutating.value) return;
    modals.hide(MODAL_NAME);
  }
</script>

<template>
  <div class="pos-v2-incoming-preview-modal">
    <div class="modal-header">
      <h2 class="modal-title">{{ $t('posV2.orderPreviewTitle') }}</h2>
      <PosUiButton
        icon="close"
        color="neutral"
        variant="ghost"
        size="md"
        :disabled="isMutating"
        @click="handleClose"
      />
    </div>

    <div class="modal-body">
      <PosV2ViewWorkspaceBase
        :order="displayOrder"
        :loading="false"
        mode="preview"
      />
    </div>

    <div class="modal-footer">
      <div class="footer-secondary">
        <PosUiButton
          color="neutral"
          variant="outline"
          size="lg"
          class="btn-close"
          :disabled="isMutating"
          @click="handleClose"
        >
          {{ $t('posV2.close') }}
        </PosUiButton>

        <PosUiButton
          v-if="deviceCanEverPrint"
          color="neutral"
          variant="outline"
          size="lg"
          icon="printer"
          class="btn-print-summary"
          :loading="printingSummary"
          :disabled="isMutating && !printingSummary"
          @click="handlePrintSummary"
        >
          {{ $t('posV2.printSummary') }}
        </PosUiButton>
      </div>

      <div class="footer-actions">
        <PosUiButton
          color="error"
          variant="soft"
          size="lg"
          icon="close"
          class="btn-reject"
          :loading="rejecting"
          :disabled="!canReject"
          @click="handleReject"
        >
          {{ $t('posV2.reject') }}
        </PosUiButton>

        <PosUiButton
          color="primary"
          variant="solid"
          size="lg"
          icon="check"
          class="btn-accept"
          :loading="accepting"
          :disabled="!canAccept"
          @click="handleAccept"
        >
          {{ $t('posV2.accept') }}
        </PosUiButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // Root lives inside .vm--modal.dialog--height-limit (a max-height flex
  // parent): the body asks for 76vh and shrinks on short screens.
  .pos-v2-incoming-preview-modal {
    @apply tw-flex tw-max-h-full tw-min-h-0 tw-w-full tw-flex-col;

    .modal-header {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-neutral-200 tw-px-4 tw-py-3;

      .modal-title {
        @apply tw-text-lg tw-font-bold tw-text-neutral-900;
      }
    }

    .modal-body {
      @apply tw-flex tw-min-h-0 tw-flex-col tw-overflow-hidden;
      height: 76vh;
    }

    .modal-footer {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-between tw-gap-2 tw-border-t tw-border-neutral-200 tw-px-4 tw-py-3;

      .footer-secondary {
        @apply tw-flex tw-items-center tw-gap-2;
      }

      .footer-actions {
        @apply tw-flex tw-items-center tw-gap-2;
      }

      :deep(.btn-close),
      :deep(.btn-print-summary),
      :deep(.btn-reject),
      :deep(.btn-accept) {
        @apply tw-min-w-[130px];
      }
    }
  }
</style>
